import { Head, Link } from '@inertiajs/react'
import { buttonClass, eyebrowClass, linkClass } from '../../components/ui'
import { sessionDate } from '../../lib/launch'
import type { DeviceOption, LaunchSessionSummary } from '../../types/launch'

export default function LaunchSessionsIndex({ sessions, devices }: { sessions: LaunchSessionSummary[]; devices: DeviceOption[] }) {
  return (
    <>
      <Head title="Sessions" />
      <header className="mb-9 flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
        <div>
          <p className={`${eyebrowClass} text-muted`}>Your range notebook</p>
          <h1 className="mt-3 font-serif text-4xl tracking-tight sm:text-5xl">Sessions</h1>
          <p className="mt-4 max-w-xl text-base leading-7 text-muted">Keep your shots together. Look at the pattern, review the outliers, and compare what changed.</p>
        </div>
        <Link href="/launch_sessions/new" className={`${buttonClass} shrink-0`}>Upload a session <span aria-hidden="true">↗</span></Link>
      </header>
      {sessions.length === 0 ? (
        <section className="rounded-xl border border-dashed border-line bg-surface px-6 py-14 text-center sm:px-12">
          <p className={`${eyebrowClass} text-muted`}>A starting point for the evidence</p>
          <h2 className="mt-4 font-serif text-3xl">Your first session starts here.</h2>
          <p className="mx-auto mt-4 max-w-lg text-sm leading-7 text-muted">Upload a CSV export or a photo of your monitor. Review individual shots or confirm averages from the screen.</p>
        </section>
      ) : (
        <ul className="divide-y divide-line overflow-hidden rounded-xl border border-line bg-surface">
          {sessions.map(session => (
            <li key={session.id} className="flex flex-col justify-between gap-4 p-6 sm:flex-row sm:items-center sm:px-8">
              <div className="min-w-0">
                <p className={`${eyebrowClass} text-muted`}>{sessionDate(session.recorded_on)} <span className="ml-2 rounded border border-line bg-soft px-2 py-1">{{ csv: 'CSV', photo: 'Photo', averages: 'Averages' }[session.source ?? 'csv']}</span></p>
                <h2 className="mt-2 break-words font-serif text-2xl"><Link href={`/launch_sessions/${session.id}`} className={linkClass}>{session.name}</Link></h2>
                <p className="mt-2 text-sm text-muted">{session.device_label ?? (session.source === 'photo' || session.source === 'averages' ? 'Photo session' : 'CSV session')}{session.clubs.length > 0 && ` · ${session.clubs.join(', ')}`}</p>
              </div>
              {session.source === 'averages' ? <p className="text-sm text-muted">Screen averages · no per-shot data</p> : session.status === 'needs_review' ? <span className="text-sm font-medium text-brand">Needs photo review</span> : session.status === 'needs_mapping' ? (
                <span className="w-fit shrink-0 rounded-full border border-line bg-soft px-3 py-1.5 text-xs font-medium text-brand">Needs column matching</span>
              ) : (
                <p className="shrink-0 font-mono text-sm tabular-nums text-muted">{session.shots_count} {session.shots_count === 1 ? 'shot' : 'shots'}</p>
              )}
            </li>
          ))}
        </ul>
      )}
      <aside className="mt-8 border-t border-line pt-6 text-sm leading-7 text-muted">
        <p><span className="font-semibold text-ink">Bring your CSV.</span> Garmin exports are recognized automatically. For other monitors, match the columns and units once; we’ll remember the layout.</p>
        {devices.length > 0 && <p className="mt-1">Automatic recognition: {devices.map(device => device.label).join(' · ')}.</p>}
      </aside>
    </>
  )
}
